/**
 * How a live repeats, the way a calendar asks it: a short menu built from the day picked, and Custom for the rest
 * (every N days, weeks, months or years, on which days, and when it stops). One line says what it means and the
 * next few dates show it is right. New room, Change the time and Change the series all draw this one picker.
 */
import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import {
  addDays,
  dayName,
  endsTail,
  firstOn,
  patternDates,
  patternProblem,
  SERIES_EVERY_MAX,
  SERIES_MAX_COUNT,
  type SeriesEnds,
  type SeriesPattern,
  type SeriesUnit,
} from "../../shared/live-series";
import { Sheet as PhoneSheet } from "../components/phone/Sheet";
import { usePhone } from "../components/phone/usePhone";
import { Button } from "../components/ui/Button";
import { Icon } from "../components/ui/Icon";
import { Select } from "../components/ui/Select";
import { Sheet } from "../components/ui/Sheet";
import { monthChoices, nextLine, patternSentence, quickChoices, quickOf, rhythmWords, startingPattern, weekOrder } from "./repeats";
import "../styles/repeats.css";

/** The first session: the day and time the host picked, in the zone it happens in. */
export interface RepeatWhen {
  date: string;
  time: string;
  timezone: string;
  /** The session the pattern starts at: later than 1 when a series is changed from its next session on. */
  fromSeq?: number;
}

/** The sentence the host reads under the control; the time is left out until one is picked. */
export function repeatSentence(p: SeriesPattern, when: RepeatWhen): string {
  return when.time ? patternSentence(p, when.date, when.time, when.timezone) : `${rhythmWords(p, when.date)}${endsTail(p.ends, firstOn(p, when.date))}`;
}

/** What a guest reads on the sign-up page: the rhythm. The card above it already says the time. */
export function repeatGuestLine(p: SeriesPattern, when: RepeatWhen): string {
  return rhythmWords(p, when.date);
}

/** "Until 15 December" or "For 10 sessions": the end, for the guest's card. */
export function endsLine(p: SeriesPattern, date: string): string {
  const words = endsTail(p.ends, firstOn(p, date)).replace(/^, /, "");
  return words.charAt(0).toUpperCase() + words.slice(1);
}

/** The plain line and the next dates, under the control. */
export function RepeatLine({ pattern, when }: { pattern: SeriesPattern; when: RepeatWhen }) {
  const problem = patternProblem(pattern, when.date, when.fromSeq);
  if (problem) {
    return (
      <p className="rpk-line rpk-bad" role="alert">
        {problem}
      </p>
    );
  }
  return (
    <div className="rpk-sum">
      <p className="rpk-line">{repeatSentence(pattern, when)}</p>
      <p className="rpk-next">{nextLine(pattern, when.date, when.fromSeq)}</p>
    </div>
  );
}

const UNITS: Array<[SeriesUnit, string]> = [
  ["day", "day"],
  ["week", "week"],
  ["month", "month"],
  ["year", "year"],
];

function Menu({
  anchor,
  when,
  value,
  onPick,
  onCustom,
  onClose,
  noOnce,
}: {
  noOnce: boolean;
  anchor: HTMLElement | null;
  when: RepeatWhen;
  value: SeriesPattern | null;
  onPick: (pattern: SeriesPattern | null) => void;
  onCustom: () => void;
  onClose: () => void;
}) {
  const phone = usePhone();
  const box = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState<{ top: number; left: number; width: number } | null>(null);
  const here = quickOf(value, when.date);

  // Why measured once drawn: under the control when it fits, above it when that fits, else against the screen's edge,
  // so no row is ever out of reach (a control low on the page, like the one in Change the time, has little room below).
  useLayoutEffect(() => {
    if (phone || !anchor || !box.current) return;
    const r = anchor.getBoundingClientRect();
    const height = box.current.offsetHeight;
    const width = Math.min(Math.max(r.width, 300), window.innerWidth - 16);
    const fitsBelow = r.bottom + 6 + height <= window.innerHeight - 8;
    const fitsAbove = r.top - 6 - height >= 8;
    const top = fitsBelow ? r.bottom + 6 : fitsAbove ? r.top - 6 - height : Math.max(8, window.innerHeight - height - 8);
    setPos({ top, left: Math.max(8, Math.min(r.left, window.innerWidth - width - 8)), width });
  }, [anchor, phone]);

  useEffect(() => {
    if (phone) return;
    const away = (e: MouseEvent) => {
      const t = e.target as Node;
      if (!box.current?.contains(t) && !anchor?.contains(t)) onClose();
    };
    const key = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.stopPropagation();
        onClose();
      }
    };
    document.addEventListener("mousedown", away);
    document.addEventListener("keydown", key, true);
    return () => {
      document.removeEventListener("mousedown", away);
      document.removeEventListener("keydown", key, true);
    };
  }, [anchor, onClose, phone]);

  const rows = (
    <>
      {quickChoices(when.date)
        .filter((c) => !(noOnce && c.id === "once"))
        .map((c) => (
        <button
          key={c.id}
          type="button"
          role="menuitemradio"
          aria-checked={here?.id === c.id}
          className="rpk-item"
          onClick={() => onPick(c.pattern && value ? { ...c.pattern, ends: value.ends } : c.pattern)}
        >
          <span>{c.label}</span>
          {here?.id === c.id ? <Icon name="check" /> : null}
        </button>
      ))}
      <button type="button" role="menuitemradio" aria-checked={!here} className="rpk-item rpk-custom" onClick={onCustom}>
        <span>Custom...</span>
        {!here ? <Icon name="check" /> : null}
      </button>
    </>
  );

  if (phone) {
    return (
      <PhoneSheet open onClose={onClose} title="Repeats" closable className="rpk-sheet">
        <div className="rpk-list" role="menu" aria-label="Repeats">
          {rows}
        </div>
      </PhoneSheet>
    );
  }
  return createPortal(
    <div
      className="rpk-menu"
      role="menu"
      aria-label="Repeats"
      ref={box}
      style={pos ? { top: pos.top, left: pos.left, width: pos.width } : { top: 0, left: 0, visibility: "hidden" }}
    >
      {rows}
    </div>,
    document.body,
  );
}

/** The custom dialog: a laptop's dialog, a phone's sheet. */
function CustomRepeat({
  when,
  value,
  onDone,
  onClose,
}: {
  when: RepeatWhen;
  value: SeriesPattern | null;
  onDone: (pattern: SeriesPattern) => void;
  onClose: () => void;
}) {
  const start = value ?? startingPattern(when.date);
  const [unit, setUnit] = useState<SeriesUnit>(start.unit);
  const [everyText, setEveryText] = useState(String(start.every));
  const [weekdays, setWeekdays] = useState<number[]>(start.weekdays);
  const [monthBy, setMonthBy] = useState<"day" | "nth">(start.monthBy);
  const [endsKind, setEndsKind] = useState<SeriesEnds["kind"]>(start.ends.kind);
  const [endDate, setEndDate] = useState(start.ends.kind === "on" ? start.ends.date : "");
  const [countText, setCountText] = useState(String(start.ends.kind === "after" ? start.ends.count : 10));

  const every = Number(everyText) || 0;
  const ends: SeriesEnds =
    endsKind === "after" ? { kind: "after", count: Number(countText) || 0 } : endsKind === "on" ? { kind: "on", date: endDate } : { kind: "never" };
  const pattern: SeriesPattern = { unit, every, weekdays: unit === "week" ? weekdays : [], monthBy, ends };
  const problem = patternProblem(pattern, when.date, when.fromSeq);
  const plural = every !== 1;
  const order = weekOrder();

  const pickEnds = (kind: SeriesEnds["kind"]) => {
    setEndsKind(kind);
    if (kind === "on" && !endDate) {
      const tenth = patternDates({ ...pattern, ends: { kind: "never" } }, when.date, 10).at(-1);
      setEndDate(tenth && tenth > when.date ? tenth : addDays(when.date, 70));
    }
  };

  return (
    <Sheet
      open
      onClose={onClose}
      title="Custom repeat"
      label="Custom repeat"
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button variant="p" icon="check" disabled={!!problem} onClick={() => onDone(pattern)}>
            Done
          </Button>
        </>
      }
    >
      <div className="rpk-form">
        <div className="rpk-every">
          <span id="rpk-every-l">Repeat every</span>
          <input
            className="inp rpk-num"
            type="number"
            inputMode="numeric"
            min={1}
            max={SERIES_EVERY_MAX}
            aria-labelledby="rpk-every-l"
            value={everyText}
            onChange={(e) => setEveryText(e.target.value)}
          />
          <Select
            className="rpk-unit"
            ariaLabel="Day, week, month or year"
            value={unit}
            options={UNITS.map(([id, word]) => ({ value: id, label: plural ? `${word}s` : word }))}
            onChange={(next) => setUnit(next as SeriesUnit)}
          />
        </div>

        {unit === "week" ? (
          <div className="rpk-blk">
            <span className="rpk-cap" id="rpk-on-l">
              Repeat on
            </span>
            <div className="rpk-days" role="group" aria-labelledby="rpk-on-l">
              {order.map((wd) => {
                const on = weekdays.includes(wd);
                return (
                  <button
                    key={wd}
                    type="button"
                    className={on ? "on" : undefined}
                    aria-label={dayName(wd)}
                    aria-pressed={on}
                    onClick={() => setWeekdays((was) => (on ? was.filter((d) => d !== wd) : [...was, wd]))}
                  >
                    {dayName(wd).slice(0, 1)}
                  </button>
                );
              })}
            </div>
          </div>
        ) : null}

        {unit === "month" ? (
          <div className="rpk-blk" role="radiogroup" aria-label="Which day of the month">
            {monthChoices(when.date).map((c) => (
              <label key={c.id} className="rpk-radio">
                <input type="radio" name="rpk-month" checked={monthBy === c.id} onChange={() => setMonthBy(c.id)} />
                <span>{c.label}</span>
              </label>
            ))}
          </div>
        ) : null}

        <div className="rpk-blk" role="radiogroup" aria-labelledby="rpk-ends-l">
          <span className="rpk-cap" id="rpk-ends-l">
            Ends
          </span>
          <label className="rpk-radio">
            <input type="radio" name="rpk-ends" checked={endsKind === "never"} onChange={() => pickEnds("never")} />
            <span>Never</span>
          </label>
          <label className="rpk-radio">
            <input type="radio" name="rpk-ends" checked={endsKind === "on"} onChange={() => pickEnds("on")} />
            <span>On</span>
            <input
              className="inp rpk-date"
              type="date"
              aria-label="The day it ends"
              min={addDays(when.date, 1)}
              value={endDate}
              onFocus={() => endsKind !== "on" && pickEnds("on")}
              onChange={(e) => setEndDate(e.target.value)}
            />
          </label>
          <label className="rpk-radio">
            <input type="radio" name="rpk-ends" checked={endsKind === "after"} onChange={() => pickEnds("after")} />
            <span>After</span>
            <input
              className="inp rpk-num"
              type="number"
              inputMode="numeric"
              min={Math.max(2, when.fromSeq ?? 1)}
              max={SERIES_MAX_COUNT}
              aria-label="How many sessions"
              value={countText}
              onFocus={() => endsKind !== "after" && pickEnds("after")}
              onChange={(e) => setCountText(e.target.value)}
            />
            <span>sessions</span>
          </label>
        </div>

        <RepeatLine pattern={pattern} when={when} />
      </div>
    </Sheet>
  );
}

/** Repeats, as one control: the menu, Custom, and the line that says what it means. */
export function RepeatPicker({
  when,
  value,
  onChange,
  chip = false,
  noOnce = false,
}: {
  when: RepeatWhen;
  value: SeriesPattern | null;
  onChange: (pattern: SeriesPattern | null) => void;
  /** Drawn as the small chip of the popovers instead of a field. */
  chip?: boolean;
  /** A series stays a series: Just once is not offered. */
  noOnce?: boolean;
}) {
  const [menu, setMenu] = useState(false);
  const [custom, setCustom] = useState(false);
  const [anchor, setAnchor] = useState<HTMLButtonElement | null>(null);
  const here = quickOf(value, when.date);
  const words = here ? here.label : value ? rhythmWords(value, when.date) : "Just once";

  const trigger = (
    <button
      ref={setAnchor}
      type="button"
      className={chip ? "rpk-chipbtn" : "inp rpk-trig"}
      data-repeat-menu=""
      aria-haspopup="menu"
      aria-expanded={menu}
      aria-label={`Repeats: ${words}`}
      disabled={!when.date}
      onClick={() => setMenu((open) => !open)}
    >
      <span className="rpk-val">{words}</span>
      <Icon name="down" />
    </button>
  );

  return (
    <div className={chip ? "rpk rpk-in" : "rpk"}>
      {chip ? (
        <span className={value ? "lsel lrepon rpk-lsel" : "lsel rpk-lsel"}>
          <small>Repeats</small>
          {trigger}
        </span>
      ) : (
        trigger
      )}
      {menu ? (
        <Menu
          noOnce={noOnce}
          anchor={anchor}
          when={when}
          value={value}
          onClose={() => setMenu(false)}
          onPick={(pattern) => {
            onChange(pattern);
            setMenu(false);
          }}
          onCustom={() => {
            setMenu(false);
            setCustom(true);
          }}
        />
      ) : null}
      {value ? <RepeatLine pattern={value} when={when} /> : null}
      {custom ? (
        <CustomRepeat
          when={when}
          value={value}
          onClose={() => setCustom(false)}
          onDone={(pattern) => {
            onChange(pattern);
            setCustom(false);
          }}
        />
      ) : null}
    </div>
  );
}
